import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TransferDialog from "@/components/TransferDialog";
import SplitTransferDialog from "@/components/SplitTransferDialog";
import RecurringTransfersDialog from "@/components/RecurringTransfersDialog";
import { formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeftRight, ArrowRight, RefreshCw, Trash2, TrendingUp, Wallet,
  Plus, Search, Zap, Bookmark, Undo2, Split, CalendarClock,
} from "lucide-react";
import { useEntity } from "@/context/EntityContext";

const SYM = { USD: "$", NGN: "₦" };

function LegBadge({ leg }) {
  if (!leg) return <span className="text-xs text-[var(--muted-text)]">—</span>;
  const Icon = leg.kind === "investment" ? TrendingUp : Wallet;
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <Icon className="w-3.5 h-3.5 text-[var(--muted-text)]" />
      <span className="font-medium">{leg.name || "Unnamed"}</span>
      <span className="text-xs text-[var(--muted-text)] uppercase tracking-wider">
        {leg.currency}
      </span>
    </span>
  );
}

export default function Transfers() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [ccyFilter, setCcyFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [undoTarget, setUndoTarget] = useState(null);
  const [reverseTarget, setReverseTarget] = useState(null);  // iter 72r
  const [transferOpen, setTransferOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const { currentEntityId } = useEntity();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ccyFilter !== "all") params.currency = ccyFilter;
      const [{ data }, { data: tpls }] = await Promise.all([
        api.get("/transfers", { params }),
        api.get("/transfer-templates").catch(() => ({ data: [] })),
      ]);
      setRows(data || []);
      setTemplates(tpls || []);
    } catch (err) {
      console.warn("[Transfers] load failed", err?.message);
      toast.error("Couldn't load transfers");
    } finally {
      setLoading(false);
    }
  }, [ccyFilter]);

  useEffect(() => { load(); }, [load, currentEntityId]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (r.memo || "").toLowerCase().includes(q) ||
        (r.from?.name || "").toLowerCase().includes(q) ||
        (r.to?.name || "").toLowerCase().includes(q) ||
        (r.transfer_id || "").toLowerCase().includes(q)
      );
    });
  }, [rows, kindFilter, search]);

  const undo = async () => {
    if (!undoTarget) return;
    try {
      const { data } = await api.delete(`/transfers/${undoTarget.transfer_id}`);
      const bits = [];
      if (data.deleted_txns) bits.push(`${data.deleted_txns} txns`);
      if (data.reversed_investment_activity) bits.push("investment position");
      toast.success(`Transfer undone · ${bits.join(" + ") || "cleaned up"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoTarget(null);
      load();
    }
  };

  // iter 72r — Reversal Wizard: post an opposite pair, keep audit trail.
  const reverse = async () => {
    if (!reverseTarget) return;
    try {
      const { data } = await api.post(`/transfers/${reverseTarget.transfer_id}/reverse`);
      toast.success(`Reversed on ${data.date} · original left as audit trail`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reverse failed");
    } finally {
      setReverseTarget(null);
      load();
    }
  };

  const postTemplate = async (tpl) => {
    try {
      const { data } = await api.post(`/transfer-templates/${tpl.id}/post`);
      const amt = data?.from?.amount ?? data?.from_amount ?? "";
      toast.success(`Posted "${tpl.name}"${amt !== "" ? ` · ${amt}` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to post "${tpl.name}"`);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/transfer-templates/${tpl.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const stats = useMemo(() => {
    const byCcy = {};
    let count = 0;
    for (const r of rows) {
      count += 1;
      const ccy = r.from?.currency || r.to?.currency || "USD";
      byCcy[ccy] = (byCcy[ccy] || 0) + (r.from?.amount || r.to?.amount || 0);
    }
    return { count, byCcy };
  }, [rows]);

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
          Journal Transfer log
        </div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-4xl mt-2">Transfers</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1 max-w-lg">
              Every move between your wallets, credit cards, and investment
              positions — reversible in one click.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="recurring-transfers-btn"
              variant="outline"
              className="rounded-md h-11 px-4"
              onClick={() => setRecurringOpen(true)}
            >
              <CalendarClock className="w-4 h-4 mr-2" /> Recurring
            </Button>
            <Button
              data-testid="split-transfer-btn"
              variant="outline"
              className="rounded-md h-11 px-4"
              onClick={() => setSplitOpen(true)}
            >
              <Split className="w-4 h-4 mr-2" /> Split
            </Button>
            <Button
              data-testid="new-transfer-btn-page"
              className="btn-brand rounded-md h-11 px-5"
              onClick={() => setTransferOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" /> New Transfer
            </Button>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
            Total transfers
          </div>
          <div className="font-display text-3xl mt-1" data-testid="transfers-stat-count">
            {stats.count}
          </div>
        </div>
        {Object.entries(stats.byCcy).slice(0, 2).map(([ccy, total]) => (
          <div key={ccy} className="card-flat p-4">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
              Volume · {ccy}
            </div>
            <div className="font-display text-3xl mt-1 font-mono-data">
              {SYM[ccy] || ""}{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
        ))}
      </div>

      {/* Templates row */}
      {templates.length > 0 && (
        <div className="mb-6" data-testid="transfer-templates-section">
          <div className="flex items-center gap-2 mb-2">
            <Bookmark className="w-4 h-4 text-[var(--brand-text)]" />
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
              One-tap templates
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                data-testid={`transfer-tpl-${tpl.id}`}
                className="group inline-flex items-center gap-1 border border-[var(--divider)] hover:border-[var(--brand)] bg-[var(--card)] rounded-full pl-3 pr-1 py-1 transition"
              >
                <button
                  onClick={() => postTemplate(tpl)}
                  data-testid={`transfer-tpl-post-${tpl.id}`}
                  className="inline-flex items-center gap-1.5 text-sm"
                  title={`Post ${SYM[tpl.currency] || ""}${tpl.amount} — ${tpl.memo || tpl.name}`}
                >
                  <Zap className="w-3.5 h-3.5 text-[var(--brand-text)]" />
                  <span className="font-medium">{tpl.name}</span>
                  <span className="text-xs text-[var(--muted-text)]">
                    · {tpl.amount.toLocaleString()}
                  </span>
                  {tpl.use_count > 0 && (
                    <span className="text-[9px] uppercase text-[var(--muted-text)] ml-1">
                      × {tpl.use_count}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => deleteTemplate(tpl)}
                  data-testid={`transfer-tpl-del-${tpl.id}`}
                  className="text-[var(--muted-text)] hover:text-[#B4283A] opacity-0 group-hover:opacity-100 transition p-1 rounded-full"
                  title="Delete template"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]" />
          <Input
            data-testid="transfers-search"
            placeholder="Search memo, account, or transfer id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-10"
          />
        </div>
        <Select value={ccyFilter} onValueChange={setCcyFilter}>
          <SelectTrigger className="w-36 h-10" data-testid="transfers-ccy-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All currencies</SelectItem>
            <SelectItem value="USD">USD only</SelectItem>
            <SelectItem value="NGN">NGN only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-44 h-10" data-testid="transfers-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="standard">Standard (wallet→wallet)</SelectItem>
            <SelectItem value="investment_buy">Investment buy</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm" variant="outline" onClick={load} disabled={loading}
          className="h-10 rounded-full"
          data-testid="transfers-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-[110px_1fr_20px_1fr_140px_120px_120px] gap-4 items-center px-4 py-3 border-b border-[var(--divider)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
          <div>Date</div>
          <div>From</div>
          <div />
          <div>To</div>
          <div className="text-right">Amount</div>
          <div>Memo</div>
          <div className="text-right">Actions</div>
        </div>

        {loading && rows.length === 0 && (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            Loading transfers…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-16 text-center">
            <ArrowLeftRight className="w-10 h-10 mx-auto text-[var(--muted-text)] opacity-40" />
            <p className="mt-4 text-sm text-[var(--muted-text)]">
              {rows.length === 0
                ? "No transfers yet. Post one and it'll show up here."
                : "No transfers match those filters."}
            </p>
          </div>
        )}

        {filtered.map((r) => (
          <div
            key={r.transfer_id}
            data-testid={`transfer-row-${r.transfer_id}`}
            className="grid grid-cols-[110px_1fr_20px_1fr_140px_120px_120px] gap-4 items-center px-4 py-3 border-b border-[var(--divider)] last:border-0 hover:bg-[var(--row-hover)] transition"
          >
            <div className="text-sm">{formatDate(r.date)}</div>
            <LegBadge leg={r.from} />
            <ArrowRight className="w-3.5 h-3.5 text-[var(--muted-text)]" />
            <LegBadge leg={r.to} />
            <div className="text-right font-mono-data text-sm">
              {r.from ? (
                <>
                  <div>{formatCurrency(r.from.amount, r.from.currency)}</div>
                  {r.to && r.from.currency !== r.to.currency && (
                    <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
                      → {formatCurrency(r.to.amount, r.to.currency)}
                      {r.fx_rate && <> @ {r.fx_rate.toFixed(4)}</>}
                    </div>
                  )}
                  {r.to && r.kind === "investment_buy" && r.to.quantity ? (
                    <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
                      · {r.to.quantity} shares
                    </div>
                  ) : null}
                </>
              ) : (
                <span className="text-[var(--muted-text)]">—</span>
              )}
            </div>
            <div className="text-xs text-[var(--muted-text)] truncate" title={r.memo || ""}>
              {r.memo || <span className="opacity-40">—</span>}
            </div>
            <div className="text-right flex items-center justify-end gap-1">
              {r.kind !== "investment_buy" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReverseTarget(r)}
                  data-testid={`transfer-reverse-${r.transfer_id}`}
                  className="text-[#2F6B4A] hover:bg-[#EEF7F2] hover:text-[#2F6B4A]"
                  title="Post a reversal (keeps audit trail)"
                >
                  <Undo2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUndoTarget(r)}
                data-testid={`transfer-undo-${r.transfer_id}`}
                className="text-[#B4283A] hover:bg-[#FCE7E9] hover:text-[#B4283A]"
                title="Delete transfer (no audit trail)"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Undo confirmation */}
      <AlertDialog open={!!undoTarget} onOpenChange={(o) => !o && setUndoTarget(null)}>
        <AlertDialogContent data-testid="transfer-undo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this transfer?</AlertDialogTitle>
            <AlertDialogDescription>
              {undoTarget && (
                <>
                  Removes both linked transactions and the associated journal
                  entries.
                  {undoTarget.kind === "investment_buy" && (
                    <> The position quantity and cost basis will also be reversed.</>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="transfer-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={undo}
              data-testid="transfer-undo-confirm"
              className="bg-[#B4283A] hover:bg-[#8f1f2e] text-white"
            >
              Undo transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse confirmation (iter 72r) — keeps audit trail */}
      <AlertDialog open={!!reverseTarget} onOpenChange={(o) => !o && setReverseTarget(null)}>
        <AlertDialogContent data-testid="transfer-reverse-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse this transfer?</AlertDialogTitle>
            <AlertDialogDescription>
              Posts a NEW opposite transfer dated today. The original stays in
              your books as an audit trail — nothing is deleted. Balances end
              up back where they were.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="transfer-reverse-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={reverse}
              data-testid="transfer-reverse-confirm"
              className="bg-[#2F6B4A] hover:bg-[#255238] text-white"
            >
              Post reversal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onDone={() => { setTransferOpen(false); load(); }}
      />
      <SplitTransferDialog
        open={splitOpen}
        onClose={() => setSplitOpen(false)}
        onDone={() => { setSplitOpen(false); load(); }}
      />
      <RecurringTransfersDialog
        open={recurringOpen}
        onClose={() => setRecurringOpen(false)}
      />
    </AppShell>
  );
}
